var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { SelectCssClass, SelectWidth, } from '../../../../components/src/components/select/select.types';
import { IressInput, IressLabel } from '../../wrappers';
import { getCssClasses } from './IressSelect.helpers';
import { transformTag } from '../../utils/helpers';
let selectIdRefs = 0;
const Select = (_a, ref) => {
    var { required, name, placeholder, children, width, hiddenLabel, className, multiInputLabel, optional, readonly, value, onChange, onBlur, onFocus } = _a, restProps = __rest(_a, ["required", "name", "placeholder", "children", "width", "hiddenLabel", "className", "multiInputLabel", "optional", "readonly", "value", "onChange", "onBlur", "onFocus"]);
    const selectIdRef = useRef(`iress-select-${selectIdRefs++}`);
    const nativeInputRef = useRef(null);
    const componentRef = useRef(null);
    const didMountRef = useRef(false);
    const fieldElRef = useRef(null);
    const [error, setError] = useState('');
    const [currentValue, setCurrentValue] = useState(value || '');
    const hostClasses = getCssClasses({
        width,
        hiddenLabel,
        required,
        className,
        error,
    });
    const dataTestid = restProps['data-testid'];
    const nativeDataTestid = dataTestid
        ? `${restProps['data-testid']}__select`
        : null;
    React.useImperativeHandle(ref, () => {
        return {
            validate() {
                handleValidate();
            },
            reset() {
                handleReset();
            },
            getNativeElement() {
                return __awaiter(this, void 0, void 0, function* () {
                    const ele = yield handleGetNativeElement();
                    return Promise.resolve(ele);
                });
            },
        };
    });
    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        var _a;
        if (!componentRef.current || didMountRef.current === true)
            return;
        didMountRef.current = true;
        // These are needed for `iress-field` and `iress-form`
        componentRef.current['validate'] = handleValidate;
        componentRef.current['reset'] = () => __awaiter(void 0, void 0, void 0, function* () {
            handleReset();
        });
        componentRef.current['getNativeElement'] =
            () => __awaiter(void 0, void 0, void 0, function* () {
                const ele = yield handleGetNativeElement();
                return Promise.resolve(ele);
            });
        // Register the select with fieldEl.
        if (nativeInputRef.current) {
            const selectedOpt = nativeInputRef.current.querySelector(`option[value="${value}"]`);
            if (selectedOpt)
                selectedOpt.setAttribute('selected', 'selected');
            fieldElRef['current'] = nativeInputRef.current.closest(transformTag('iress-field'));
            (_a = fieldElRef.current) === null || _a === void 0 ? void 0 : _a.addEventListener('iressFieldDidLoad', () => __awaiter(void 0, void 0, void 0, function* () {
                var _b;
                yield ((_b = fieldElRef.current) === null || _b === void 0 ? void 0 : _b.registerInputElement(getStatus()));
            }));
        }
    });
    /* eslint-enable react-hooks/exhaustive-deps */
    const getStatus = useCallback((newValue) => ({
        value: newValue !== undefined ? newValue : currentValue,
        element: nativeInputRef.current
            ? nativeInputRef.current
            : undefined,
        name: name ? name : selectIdRef.current,
        required: required || false,
        error: error,
        label: multiInputLabel,
    }), [currentValue, error, multiInputLabel, name, required]);
    const handleValidate = () => {
        const fieldElError = fieldElRef.current && fieldElRef.current.error
            ? fieldElRef.current.error
            : '';
        nativeInputRef.current &&
            updateErrors(nativeInputRef.current.validationMessage || fieldElError);
    };
    const handleReset = () => __awaiter(void 0, void 0, void 0, function* () {
        setError('');
        setCurrentValue('');
    });
    const handleGetNativeElement = () => __awaiter(void 0, void 0, void 0, function* () { return Promise.resolve(nativeInputRef.current); });
    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        const nativeInput = nativeInputRef === null || nativeInputRef === void 0 ? void 0 : nativeInputRef.current;
        const fieldEl = fieldElRef === null || fieldElRef === void 0 ? void 0 : fieldElRef.current;
        const status = getStatus();
        return () => {
            if (nativeInput &&
                fieldEl &&
                status &&
                typeof fieldEl.unregisterInputElement === 'function') {
                fieldEl.unregisterInputElement(status);
            }
        };
    }, []);
    /* eslint-enable react-hooks/exhaustive-deps */
    useEffect(() => {
        const reg = () => __awaiter(void 0, void 0, void 0, function* () {
            var _a;
            if (typeof ((_a = fieldElRef === null || fieldElRef === void 0 ? void 0 : fieldElRef.current) === null || _a === void 0 ? void 0 : _a.registerInputElement) !== 'function')
                return;
            yield fieldElRef.current.registerInputElement(getStatus());
        });
        reg();
    }, [required, fieldElRef, getStatus]);
    const getOptionsFromChildren = () => {
        var _a;
        let childStr = '';
        let optsArr = [];
        const opts = (_a = nativeInputRef.current) === null || _a === void 0 ? void 0 : _a.getElementsByTagName('option');
        if (opts)
            optsArr = Array.from(opts);
        if (optsArr.length > 0) {
            childStr = JSON.stringify(optsArr.map((opt) => {
                return opt.value;
            }));
        }
        return childStr;
    };
    useEffect(() => {
        if ((children === null || children === void 0 ? void 0 : children.length) > 0 &&
            getOptionsFromChildren().includes(currentValue) === false)
            setCurrentValue('');
    }, [children]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => {
        setCurrentValue(value || '');
    }, [value]);
    const updateErrors = (valMsg) => {
        if ((error !== '' && valMsg === '') ||
            (error === '' && valMsg !== '')) {
            if (nativeInputRef.current &&
                nativeInputRef.current.value !== currentValue) {
                setCurrentValue(nativeInputRef.current.value);
            }
            const newStatus = Object.assign(Object.assign({}, getStatus()), { error: valMsg });
            const event = new CustomEvent('iressErrorChange', {
                bubbles: true,
                detail: newStatus,
            });
            // Dispatch the event.
            nativeInputRef.current &&
                nativeInputRef.current.dispatchEvent(event);
        }
        setError(valMsg);
    };
    const getSelectedOptionText = (selectedValue, childrenArr) => {
        if (!selectedValue)
            return undefined;
        const selectedOption = value
            ? childrenArr.filter((child) => child.props.value === selectedValue)
            : null;
        return selectedOption && selectedOption.length > 0
            ? selectedOption[0].props.children
            : '';
    };
    const handleChange = (event, changeHandler) => {
        const el = nativeInputRef.current;
        if (el && el.value !== currentValue) {
            setCurrentValue(el.value);
        }
        changeHandler && changeHandler(event.nativeEvent);
    };
    const handleFocus = (event, focusHandler) => {
        focusHandler && focusHandler(event.nativeEvent);
    };
    const handleBlur = (event, blurHandler) => {
        blurHandler && blurHandler(event.nativeEvent);
    };
    return (React.createElement("div", Object.assign({ className: hostClasses, "data-testid": dataTestid, ref: componentRef }, restProps),
        multiInputLabel && (React.createElement(IressLabel, { className: SelectCssClass.Label, for: selectIdRef.current, labelText: multiInputLabel, required: required, optional: optional, hiddenLabel: hiddenLabel })),
        !readonly ? (React.createElement("div", { className: SelectCssClass.Wrapper },
            React.createElement("select", { name: name ? name : selectIdRef.current, id: selectIdRef.current, className: SelectCssClass.Select, required: required, "data-testid": nativeDataTestid, value: currentValue, onChange: (e) => handleChange(e, onChange), onBlur: (e) => handleBlur(e, onBlur), onFocus: (e) => handleFocus(e, onFocus), ref: nativeInputRef },
                placeholder && React.createElement("option", { value: "" }, placeholder),
                children))) : (React.createElement("div", null,
            React.createElement(IressInput, { readonly: true, width: width, required: required, value: getSelectedOptionText(value, children), onInput: undefined }),
            React.createElement("input", { name: name, id: selectIdRef.current, required: required, "data-testid": nativeDataTestid, type: "hidden", value: value, readOnly: true })))));
};
const IressSelect = React.forwardRef(Select);
IressSelect.displayName = 'IressSelect';
IressSelect.Width = SelectWidth;
export { IressSelect };
//# sourceMappingURL=IressSelect.js.map